import useCurrentSubscriptionHelper from 'hooks/billing/useCurrentSubscriptionHelper';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Addon, Feature } from '../../../../../../../../pb/subscription/v1/addon_pb';

const SEPARATOR = ' • ';

const isUnlimited = (addon: Addon | undefined) => addon?.value === '-1';

export interface PlanFeatureSummary {
    /** `handle` of the Shopify pricing plan, e.g. "free" */
    handle: string;
    /** e.g. "1 carrier • 50 orders / month" */
    summary: string;
}

/**
 * One feature line per Shopify pricing plan, built from the plan's addons.
 * Parts whose addon is missing from the plan are left out.
 */
export const usePlanFeatureSummaries = (): PlanFeatureSummary[] => {
    const { t } = useTranslation();
    const { plans } = useCurrentSubscriptionHelper();

    return useMemo(
        () =>
            plans.map(({ handle, addons }) => {
                const findAddon = (feature: Feature) => addons.find((addon) => addon.name === feature);

                const providerLimit = findAddon(Feature.PACKAGE_PROVIDER_LIMIT);
                const orderLimit = findAddon(Feature.MONTHLY_ORDER_LIMIT);

                const parts: string[] = [];

                if (findAddon(Feature.UNLIMITED_SHIPPING_PROVIDERS) || isUnlimited(providerLimit)) {
                    parts.push(t('DASHBOARD.WELCOME_CARD.PLAN_FEATURES.UNLIMITED_CARRIERS'));
                } else if (providerLimit) {
                    parts.push(
                        t('DASHBOARD.WELCOME_CARD.PLAN_FEATURES.CARRIERS', { count: parseInt(providerLimit.value, 10) })
                    );
                }

                if (isUnlimited(orderLimit)) {
                    parts.push(t('DASHBOARD.WELCOME_CARD.PLAN_FEATURES.UNLIMITED_ORDERS'));
                } else if (orderLimit) {
                    parts.push(
                        t('DASHBOARD.WELCOME_CARD.PLAN_FEATURES.ORDERS', { count: parseInt(orderLimit.value, 10) })
                    );
                }

                return { handle, summary: parts.join(SEPARATOR) };
            }),
        [plans, t]
    );
};
